import React, { useState, useRef } from "react";
import { AlertTriangle, Paperclip, Trash2, Upload, X } from "lucide-react";
import { Modal } from "./Modal";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

const REASONS = [
  { value: "damaged", label: "Damaged" },
  { value: "rusted", label: "Rusted" },
  { value: "burned_electronic", label: "Burned electronic" },
  { value: "over_pressurized", label: "Over pressurized" },
  { value: "fitting_damaged", label: "Fitting damaged" },
  { value: "other", label: "Other (specify)" },
];

export function UnserviceableModal({ itemId, itemDescription, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [reasonOther, setReasonOther] = useState("");
  const [notes, setNotes] = useState("");
  const [confirmNotFit, setConfirmNotFit] = useState(false);
  const [removeFromWorklist, setRemoveFromWorklist] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploads, setUploads] = useState([]); // {id, filename}
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!reason) return toast.error("Pick a reason");
    if (reason === "other" && !reasonOther.trim()) return toast.error("Describe the reason");
    if (!notes.trim()) return toast.error("Technician notes are required");
    if (!confirmNotFit) return toast.error("Tick the not-fit-for-calibration confirmation");
    setBusy(true);
    try {
      await api.post(`/job-items/${itemId}/unserviceable`, {
        reason,
        reason_other: reasonOther.trim() || null,
        technician_notes: notes.trim(),
        confirm_not_fit: confirmNotFit,
        remove_from_worklist: removeFromWorklist,
        attachment_ids: uploads.map((u) => u.id),
      });
      toast.success("Item raised as Unserviceable — admin will review");
      onDone && onDone();
      onClose && onClose();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await api.post(`/job-items/${itemId}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setUploads((u) => [...u, { id: r.data.id, filename: r.data.filename }]);
      }
      toast.success("Photo(s) attached");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Modal open onClose={onClose} icon={AlertTriangle}
      title="Mark as Unserviceable"
      subtitle={itemDescription ? `${itemDescription} · awaiting admin review` : "Tech action"}
      footer={
        <>
          <button className="btn ghost" onClick={onClose} disabled={busy}
            data-testid="unserviceable-cancel">Cancel</button>
          <button className="btn gold" onClick={submit}
            disabled={busy || !reason || !notes.trim() || !confirmNotFit ||
                      (reason === "other" && !reasonOther.trim())}
            data-testid="unserviceable-submit">
            {busy ? "Submitting…" : "Mark Unserviceable"}
          </button>
        </>
      }>
      <div className="notice" style={{ background: "#fff8e6", border: "1px solid #e3d28a",
        marginBottom: 12 }}>
        <AlertTriangle size={16} />
        <span style={{ fontSize: 12.5 }}>
          This <b>flags the item for admin review</b> only — the equipment is <b>not</b> permanently
          scrapped. Admin will decide whether to return to customer, return to you with instructions,
          or close it.
        </span>
      </div>

      <div className="form-grid">
        <div className="fld">
          <label>Reason <span className="req">*</span></label>
          <select className="input" value={reason} onChange={(e) => setReason(e.target.value)}
            data-testid="unserviceable-reason">
            <option value="">— Select reason —</option>
            {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        {reason === "other" && (
          <div className="fld">
            <label>Describe the reason <span className="req">*</span></label>
            <input className="input" value={reasonOther}
              onChange={(e) => setReasonOther(e.target.value)}
              placeholder="e.g. cracked LCD"
              data-testid="unserviceable-reason-other" />
          </div>
        )}
        <div className="fld" style={{ gridColumn: "span 2" }}>
          <label>Technician notes <span className="req">*</span></label>
          <textarea className="input" rows={3} value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What you found, observations, readings… (visible to admin)"
            data-testid="unserviceable-notes" />
        </div>
        <div className="fld" style={{ gridColumn: "span 2" }}>
          <label>Photos / evidence (optional)</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button type="button" className="btn ghost sm" onClick={() => fileRef.current?.click()}
              disabled={uploading} data-testid="unserviceable-upload">
              <Upload size={13} /> {uploading ? "Uploading…" : "Add photo"}
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple
              style={{ display: "none" }}
              onChange={(e) => uploadFiles(Array.from(e.target.files || []))} />
            {uploads.length > 0 && (
              <span className="t-mut" style={{ fontSize: 12 }}>
                {uploads.length} attached
              </span>
            )}
          </div>
          {uploads.length > 0 && (
            <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none", display: "flex", gap: 6, flexWrap: "wrap" }}>
              {uploads.map((u) => (
                <li key={u.id} className="tag-soft mono" style={{ fontSize: 11 }}>
                  <Paperclip size={11} style={{ verticalAlign: "-2px", marginRight: 3 }} />
                  {u.filename}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 16,
        cursor: "pointer" }} data-testid="unserviceable-confirm-label">
        <input type="checkbox" checked={confirmNotFit}
          onChange={(e) => setConfirmNotFit(e.target.checked)}
          data-testid="unserviceable-confirm" style={{ marginTop: 3 }} />
        <span style={{ fontSize: 13 }}><b>This item is not fit for calibration.</b></span>
      </label>
      <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 8,
        cursor: "pointer" }}>
        <input type="checkbox" checked={removeFromWorklist}
          onChange={(e) => setRemoveFromWorklist(e.target.checked)}
          data-testid="unserviceable-remove" style={{ marginTop: 3 }} />
        <span style={{ fontSize: 13 }}>Remove from my active worklist.</span>
      </label>
    </Modal>
  );
}
